import React, { useState } from 'react';
import Tabs from 'react-bootstrap/Tabs';
import { Col, Form, Nav, NavDropdown, Row, Tab } from 'react-bootstrap';
import styles from '../TransactionSimpleDetail.module.css';
import { getColoredHttpMethod } from '../../../../utils/commonUtil';
import _ from 'lodash';
import CopyToClipboardButton from '../../../buttons/CopyToClipboardButton';
import BzmIcon from '../../../icons/BzmIcon';
import BlazeTable from '../../../blazeTable/BlazeTable';
import CheckBox from '../../../ui/checkBox/CheckBox';
import RedirectToLiveDetail from './RedirectToLiveDetail';
import ProcessingActions from '../../../../views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/ProcessingActions';
import { DelayType, MockService } from '../../../../types/rest/mockServiceTypes';
import { convertToRMI, getFullTransactionUrl } from '../../../../utils/transactionUtils';
import { getMockServiceEndpoint } from '../../../../utils/mockServiceUtils';
import { Credentials, MatcherDsl } from '../../../../types/rest/dslTypes';
import { RequestMatcherItem } from '../../../transactionEditor/RequestMatchersList';
import { BlazeTableColDef, BlazeTableOptions } from '../../../blazeTable/BlazeTable.types';
import { MockServiceTemplate } from '../../../../store/mockServiceTemplates/mockServiceTemplateTypes';
import { HttpTransaction } from '../../../../types/rest/assetCatalogTypes';
import { useTranslation } from 'react-i18next';
import { buildBody, getBodyMatchers, getThinkTimeDom, getDetailTitle } from '../../../../utils/TransactionDetailUtils';
import { convertToHI } from '../../../../views/AssetCatalog/Transactions/TransactionEditor/TransactionEditor';
import i18n from '../../../../i18n';

interface Props {
  context: MockService | MockServiceTemplate;
  transaction: HttpTransaction;
  showTransactionLink: boolean;
}

const responseColsDefinition: BlazeTableColDef[] = [
  {
    header: i18n.t('transactions:editor.matcherName'),
    accessor: 'name',
  },
  {
    header: i18n.t('transactions:editor.value'),
    accessor: 'value',
  },
];

export const getColsTableDefinition = (conDef: BlazeTableColDef[], noResults: string): BlazeTableOptions => {
  return {
    columnDef: conDef,
    pageable: false,
    selectable: false,
    isRowExpandable: () => false,
    showRowExpander: false,
    tableName: 'responseTable',
    noResultsText: noResults,
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
  };
};

export const responseTblOpts = getColsTableDefinition(
  responseColsDefinition,
  i18n.t('transactions:editor.emptyHeaderListMessage'),
);

const HttpTransactionSimpleTabs = ({ context, transaction, showTransactionLink }: Props) => {
  const [cookiesTabActive, setCookiesTabActive] = useState(false);

  const [credentialsTabActive, setCredentialsTabActive] = useState(false);

  const [bodyTabActive, setBodyTabActive] = useState(false);

  const [moreTabActive, setMoreTabActive] = useState(false);

  const [moreTabActiveInResponse, setMoreTabActiveInResponse] = useState(false);

  const [proxyTabActive, setProxyTabActive] = useState(false);

  const [thinkTimeTabActive, setThinkTimeTabActive] = useState(false);
  const [redirectTabActive, setRedirectTabActive] = useState(false);

  const { t } = useTranslation('transactions');

  const convertToRMIs = (idPrefix: string, matchers?: MatcherDsl[]): RequestMatcherItem[] => {
    if (!matchers) {
      return [];
    }
    return matchers.map((m, ix) => convertToRMI(m, idPrefix + '-' + ix));
  };

  const requestDsl = {
    headers: convertToRMIs('matchers-header', transaction.dsl.requestDsl.headers),
    queryParams: convertToRMIs('matchers-query-params', transaction.dsl.requestDsl.queryParams),
    cookies: convertToRMIs('matchers-cookies', transaction.dsl.requestDsl.cookies),
    credentials: _.isEmpty(transaction.dsl.requestDsl.credentials)
      ? null
      : (transaction.dsl.requestDsl.credentials as Credentials),
    body: transaction.dsl.requestDsl.body ? buildBody(transaction.dsl.requestDsl.body) : [],
  };

  const responseDsl = {
    proxyUrl: transaction.dsl.responseDsl.proxyUrl,
    headers: convertToHI(transaction.dsl.responseDsl.headers),
    content: transaction.dsl.responseDsl.binary
      ? transaction.dsl.responseDsl.content
        ? transaction.dsl.responseDsl.content
        : ''
      : transaction.dsl.responseDsl.content
      ? Buffer.from(transaction.dsl.responseDsl.content, 'base64').toString()
      : '',
    binary: transaction.dsl.responseDsl.binary,
    responseDelay: transaction.dsl.responseDsl.responseDelay,
  };

  const getMatcherName = () => {
    if (_.get(transaction, 'dsl.requestDsl.url.matcherName') === 'equals_url') {
      return t('txn.equalsUrl');
    }
    if (_.get(transaction, 'dsl.requestDsl.url.matcherName') === 'matches_url') {
      return t('txn.matchesUrl');
    }
    return _.get(transaction, 'dsl.requestDsl.url.matcherName');
  };

  const handleSelect = (eventKey: any) => {
    if (eventKey === 'more-cookies') {
      setBodyTabActive(false);
      setCredentialsTabActive(false);
      setCookiesTabActive(true);
      setMoreTabActive(true);
    } else if (eventKey === 'more-credentials') {
      setBodyTabActive(false);
      setCookiesTabActive(false);
      setCredentialsTabActive(true);
      setMoreTabActive(true);
    } else if (eventKey === 'more-body') {
      setCredentialsTabActive(false);
      setCookiesTabActive(false);
      setBodyTabActive(true);
      setMoreTabActive(true);
    } else {
      setMoreTabActive(false);
    }
  };

  const handleResponseSelect = (eventKey: any) => {
    if (eventKey.startsWith('more-')) {
      setMoreTabActiveInResponse(true);
    } else {
      setMoreTabActiveInResponse(false);
    }

    //Disable all by default
    setProxyTabActive(false);
    setThinkTimeTabActive(false);
    setRedirectTabActive(false);

    switch (eventKey) {
      case 'more-response-proxy':
        setProxyTabActive(true);
        break;
      case 'more-response-thinkTime':
        setThinkTimeTabActive(true);
        break;
      case 'more-response-redirectToLive':
        setRedirectTabActive(true);
        break;
    }
  };

  const colsDefinition: BlazeTableColDef[] = [
    {
      header: t('editor.matcherName'),
      accessor: 'name',
    },
    {
      header: t('editor.matcherOperator'),
      accessor: 'operator',
    },
    {
      header: t('editor.matcherValue'),
      accessor: 'value',
    },
  ];

  const queryParamsColDefs: BlazeTableColDef[] = [
    {
      header: t('editor.required'),
      accessor: 'required',
      cellComponent: (item: RequestMatcherItem) => {
        const isRequired = item.isRequired === true;
        return (
          <CheckBox
            title={isRequired ? 'Query Param Required' : 'Query Param is Optional'}
            disabled={true}
            onCheck={_.noop}
            checked={isRequired}
          />
        );
      },
    },
    {
      header: t('editor.matcherName'),
      accessor: 'name',
    },
    {
      header: t('editor.matcherOperator'),
      accessor: 'operator',
    },
    {
      header: t('editor.matcherValue'),
      accessor: 'value',
    },
  ];

  const requestTblOpts: BlazeTableOptions = {
    columnDef: colsDefinition,
    pageable: false,
    selectable: false,
    isRowExpandable: () => false,
    showRowExpander: false,
    tableName: 'requestTable',
    customHeaderClassName: 'flex-table-item-top',
    customTableClassName: 'flex-table-simple flex-table-condensed',
  };

  const queryParamTblOptions: BlazeTableOptions = { ...requestTblOpts, columnDef: queryParamsColDefs };

  const isThinkTimeSet = (): boolean => {
    return !_.isNull(responseDsl.responseDelay) && responseDsl.responseDelay?.type !== DelayType.NONE;
  };

  const isRedirectToLiveActive = (): boolean => {
    return !_.isNull(transaction.dsl.redirectUrl) && !_.isEmpty(transaction.dsl.redirectUrl);
  };

  const fullTransactionUrl = () => {
    return getFullTransactionUrl(
      getMockServiceEndpoint(context as MockService),
      transaction.dsl.requestDsl.url.matchingValue,
    );
  };

  const getRequestMatchersTab = () => {
    return (
      <div className="p-4 bg-light">
        <Row>
          <div className={styles.titleRequestTab}>
            <Form.Label>{t('txn.method')}: </Form.Label>
          </div>
          <Col>
            <span className={styles.value} id="qa-txn-simple-detail-req-method">
              {getColoredHttpMethod(transaction.dsl.requestDsl.method as any)}
            </span>
          </Col>
        </Row>
        <Row>
          <div className={styles.titleRequestTab}>
            <Form.Label>{t('txn.url')}: </Form.Label>
          </div>
          <Col className={styles.value}>
            <span id="qa-txn-simple-detail-req-url">
              {_.get(transaction, 'dsl.requestDsl.url.matchingValue')}
              {showTransactionLink && (
                <CopyToClipboardButton textToCopy={fullTransactionUrl()} title={'Copy to Clipboard'}>
                  <a className={'btn-no-hover'} href={fullTransactionUrl()}>
                    <BzmIcon variant={'icon-bzm-copy'} />
                  </a>
                </CopyToClipboardButton>
              )}
            </span>
          </Col>
        </Row>
        <Row>
          <div className={styles.titleRequestTab}>
            <Form.Label>{t('txn.matcher')}: </Form.Label>
          </div>
          <Col>
            <span className={styles.value} id="qa-txn-simple-detail-req-matcher-name">
              {getMatcherName()}
            </span>
          </Col>
        </Row>

        <Tab.Container defaultActiveKey="headers">
          <Nav variant="tabs" onSelect={handleSelect}>
            <Nav.Item>
              <Nav.Link eventKey="headers">
                {getDetailTitle(t('editor.headersTitle'), requestDsl.headers.length)}{' '}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item onSelect={handleSelect}>
              <Nav.Link eventKey="query-params">
                {getDetailTitle(t('editor.queryParamsTitle'), requestDsl.queryParams.length)}
              </Nav.Link>
            </Nav.Item>

            <NavDropdown title="More" id="nav-dropdown" active={moreTabActive}>
              <NavDropdown.Item eventKey="more-cookies">
                {getDetailTitle(t('editor.cookiesTitle'), requestDsl.cookies.length)}
                {cookiesTabActive && (
                  <span className="indicator indicator-active indicator-sm float-right mt-3">
                    <span className="sr-only">{t('editor.activeIndicator')}</span>
                  </span>
                )}
              </NavDropdown.Item>
              <NavDropdown.Item eventKey="more-credentials">
                {getDetailTitle(
                  t('editor.credentialsTitle'),
                  !_.isNull(requestDsl.credentials) && !_.isNull(requestDsl.credentials.username)
                    ? `${t('editor.defined')}`
                    : `${t('editor.notDefined')}`,
                )}
                {credentialsTabActive && (
                  <span className="indicator indicator-active indicator-sm float-right mt-3">
                    <span className="sr-only">{t('editor.activeIndicator')}</span>
                  </span>
                )}
              </NavDropdown.Item>
              <NavDropdown.Item eventKey="more-body">
                {getDetailTitle(t('editor.bodyTitle'), requestDsl.body.length)}
                {bodyTabActive && (
                  <span className="indicator indicator-active indicator-sm float-right mt-3">
                    <span className="sr-only">{t('editor.activeIndicator')}</span>
                  </span>
                )}
              </NavDropdown.Item>
            </NavDropdown>
          </Nav>

          <Tab.Content>
            <Tab.Pane eventKey="headers">
              {requestDsl.headers.length > 0 && (
                <React.Fragment>
                  <p>{t('editor.nonEmptyListMessage')}</p>
                  <BlazeTable options={requestTblOpts} data={requestDsl.headers} />
                </React.Fragment>
              )}
              {requestDsl.headers.length === 0 && <p className={'mt-4'}>{t('editor.emptyHeaderListMessage')}</p>}
            </Tab.Pane>
            <Tab.Pane eventKey="query-params">
              {requestDsl.queryParams.length > 0 && (
                <React.Fragment>
                  <p>{t('editor.nonEmptyListMessage')}</p>
                  <BlazeTable options={queryParamTblOptions} data={requestDsl.queryParams} />
                </React.Fragment>
              )}
              {requestDsl.queryParams.length === 0 && <p className={'mt-4'}>{t('editor.emptyQueryListMessage')}</p>}
            </Tab.Pane>
            <Tab.Pane eventKey="more-cookies">
              {requestDsl.cookies.length > 0 && (
                <React.Fragment>
                  <p>{t('editor.nonEmptyListMessage')}</p>

                  <BlazeTable options={requestTblOpts} data={requestDsl.cookies} />
                </React.Fragment>
              )}

              {requestDsl.cookies.length === 0 && <p className={'mt-4'}>{t('editor.emptyCookieListMessage')}</p>}
            </Tab.Pane>
            <Tab.Pane eventKey="more-credentials">
              {requestDsl.credentials && requestDsl.credentials.username && (
                <React.Fragment>
                  <p>{t('editor.nonEmptyListMessage')}</p>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="qa-input-creds-username">
                        <Form.Label>{t('editor.username')}</Form.Label>
                        <Form.Control
                          name="username"
                          placeholder={t('editor.username')}
                          readOnly={true}
                          value={requestDsl.credentials.username}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="qa-input-creds-password">
                        <Form.Label>{t('editor.password')}</Form.Label>
                        <Form.Control
                          name="password"
                          type="text"
                          className={'passwordInput'}
                          readOnly={true}
                          placeholder={t('editor.password')}
                          value={requestDsl.credentials.password}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </React.Fragment>
              )}

              {!requestDsl.credentials ||
                (!requestDsl.credentials.username && <p className={'mt-4'}>{t('editor.noCredentialsMessage')}</p>)}
            </Tab.Pane>
            <Tab.Pane eventKey={'more-body'}>
              {requestDsl.body.length > 0 && (
                <>
                  <p>{t('editor.nonEmptyListMessage')}</p>
                  {getBodyMatchers(requestDsl.body)}
                </>
              )}
              {requestDsl.body.length === 0 && <p className={'mt-4'}>{t('editor.emptyBodyMatcherListMessage')}</p>}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </div>
    );
  };

  const getResponseTabs = () => (
    <div className="p-4 bg-light">
      <Row>
        <div className={styles.titleResponseTab}>
          <Form.Label>{t('txn.statusCode')}:</Form.Label>
        </div>
        <div>
          <span className={styles.value} id="qa-txn-simple-detail-resp-sc">
            {transaction.dsl.responseDsl.status}
          </span>
        </div>
      </Row>
      <Row>
        <div className={styles.titleResponseTab}>
          <Form.Label>{t('txn.statusMsg')}:</Form.Label>
        </div>
        <div>
          <span className={styles.value} id="qa-txn-simple-detail-resp-sm">
            {transaction.dsl.responseDsl.statusMessage}
          </span>
        </div>
      </Row>
      <Tab.Container defaultActiveKey="response-body">
        <Nav variant="tabs" onSelect={handleResponseSelect}>
          <Nav.Item>
            <Nav.Link eventKey="response-body">
              {getDetailTitle(
                t('editor.bodyTitle'),
                !_.isEmpty(responseDsl.content) ? `${t('editor.defined')}` : `${t('editor.notDefined')}`,
              )}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item onSelect={handleSelect}>
            <Nav.Link eventKey="response-headers">
              {getDetailTitle(t('editor.responseHeaders'), responseDsl.headers.length)}
            </Nav.Link>
          </Nav.Item>
          <NavDropdown title="More" id="nav-dropdown" active={moreTabActiveInResponse} onSelect={handleResponseSelect}>
            <NavDropdown.Item eventKey="more-response-proxy">
              {getDetailTitle(
                t('editor.proxyUrl'),
                !_.isNull(responseDsl.proxyUrl) && !_.isNull(responseDsl.proxyUrl)
                  ? `${t('editor.defined')}`
                  : `${t('editor.notDefined')}`,
              )}
              {proxyTabActive && (
                <span className="indicator indicator-active indicator-sm float-right mt-3">
                  <span className="sr-only">{t('editor.activeIndicator')}</span>
                </span>
              )}
            </NavDropdown.Item>
            <NavDropdown.Item eventKey="more-response-thinkTime">
              {getDetailTitle(
                t('common:thinkTime.title'),
                isThinkTimeSet() ? `${t('editor.defined')}` : `${t('editor.notDefined')}`,
              )}
              {thinkTimeTabActive && (
                <span className="indicator indicator-active indicator-sm float-right mt-3">
                  <span className="sr-only">{t('editor.activeIndicator')}</span>
                </span>
              )}
            </NavDropdown.Item>
            <NavDropdown.Item eventKey="more-response-redirectToLive">
              {getDetailTitle(
                t('common:redirectToLive.title'),
                isRedirectToLiveActive() ? `${t('editor.defined')}` : `${t('editor.notDefined')}`,
              )}
              {redirectTabActive && (
                <span className="indicator indicator-active indicator-sm float-right mt-3">
                  <span className="sr-only">{t('editor.activeIndicator')}</span>
                </span>
              )}
            </NavDropdown.Item>
          </NavDropdown>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="response-body">
            {responseDsl.content && (
              <Row className="mb-3">
                <Col>
                  {!responseDsl.binary && <Form.Label>{t('editor.rawBody')}</Form.Label>}
                  {responseDsl.binary && (
                    <React.Fragment>
                      <Form.Label>{t('editor.binaryBody')}</Form.Label>
                      <div className="float-right">{t('transactions:editor.shortBinaryDescription')}</div>
                    </React.Fragment>
                  )}
                  <Form.Control as="textarea" rows={5} name="content" value={responseDsl.content} readOnly={true} />
                </Col>
              </Row>
            )}

            {!responseDsl.content && <p className={'mt-4'}>{t('editor.noResponseBody')}</p>}
          </Tab.Pane>
          <Tab.Pane eventKey="response-headers">
            {responseDsl.headers.length > 0 && <BlazeTable options={responseTblOpts} data={responseDsl.headers} />}
            {responseDsl.headers.length === 0 && <p className={'mt-4'}>{t('editor.noResponseHeaders')}</p>}
          </Tab.Pane>
          <Tab.Pane eventKey="more-response-proxy">
            {responseDsl.proxyUrl && (
              <Row className="mb-3">
                <Col>
                  <Form.Label>{t('editor.proxyUrl')}</Form.Label>
                  <Form.Control name="proxyUrl" value={responseDsl.proxyUrl} readOnly={true} />
                </Col>
              </Row>
            )}
            {!responseDsl.proxyUrl && <p className={'mt-4'}>{t('editor.noProxyUrlDefinedMsg')}</p>}
          </Tab.Pane>
          <Tab.Pane eventKey="more-response-thinkTime">
            {isThinkTimeSet() && (
              <Row className="mb-1">
                <Col>{getThinkTimeDom(responseDsl.responseDelay)}</Col>
              </Row>
            )}
            {!isThinkTimeSet() && <p className={'mt-4'}>{t('editor.noThinkTimeDefinedMsg')}</p>}
          </Tab.Pane>
          <Tab.Pane eventKey="more-response-redirectToLive">
            <RedirectToLiveDetail transaction={transaction} />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );

  return (
    <Tabs id="qa-tabs-simple-detail" className="mb-0">
      {/* Request Tab */}
      <Tab id="qa-tab-request" title={t('txn.requestMatcher')} eventKey="request">
        {getRequestMatchersTab()}
      </Tab>
      {/* ./ Request Tab */}

      {/* Response Tab */}
      <Tab id="qa-tab-response" title={t('txn.response')} eventKey="response">
        {getResponseTabs()}
      </Tab>
      {/* ./Response Tab */}

      {/* Processing Actions */}
      <Tab id="qa-tab-processing-actions" title={t('processingActions.title')} eventKey="processingActions">
        <div className="bg-light">
          <ProcessingActions
            key={transaction.id}
            transactionId={transaction.id}
            isHeaderHidden={true}
            serviceId={transaction.serviceId}
            tableOptions={{
              isSimple: true,
              isSelectable: false,
              columns: {
                name: true,
                type: true,
                conditions: true,
                actions: false,
              },
            }}
          />
        </div>
      </Tab>
      {/* ./Processing Actions */}
    </Tabs>
  );
};

export default HttpTransactionSimpleTabs;
